import { activityFetch as fetch } from '../../components/feedback/requestActivity'
import { LoadingState } from '../../components/feedback/LoadingState'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Activity, AlertTriangle, Check, ChevronDown, CircleDashed, CircleStop, Clock,
  ExternalLink, FlaskConical, Globe, HelpCircle, Layers, PlayCircle, RefreshCw,
  Rocket, Search, ShieldAlert, Timer, Wrench, X,
} from 'lucide-react'
import './style.css'

// This screen answers one question only: what is each service doing right now.
// Time-ordered records live on the operation history screen, so nothing here is
// rendered as a feed. Recorded intent and host observation stay side by side,
// because a mismatch between them is the thing an operator must see first.

const ENVIRONMENT_LABEL = { development: '개발', production: '운영' }
const ENVIRONMENT_MARK = { development: FlaskConical, production: Globe }
// One meaning, one mark. The same icon never stands for two things on this
// screen, so an operator learns the vocabulary once and reads it everywhere.
const STATUS = {
  operating: { label: '운영 중', tone: 'live', icon: Activity },
  stopped: { label: '중단', tone: 'halted', icon: CircleStop },
  maintenance: { label: '점검', tone: 'paused', icon: Wrench },
  drift: { label: '상태 불일치', tone: 'drift', icon: AlertTriangle },
  unverified: { label: '확인 불가', tone: 'unknown', icon: HelpCircle },
  unset: { label: '미설정', tone: 'unknown', icon: CircleDashed },
}
const DESIRED_LABEL = { running: '운영', stopped: '중단', maintenance: '점검' }
const OBSERVED_LABEL = {
  running: '대상 실행 중', notRunning: '대상 중지 또는 준비 미완료', absent: '대상 없음',
  unverified: '대상 관측 불가',
}
const ACTIONS = [
  { state: 'running', label: '운영으로 기록', icon: PlayCircle },
  { state: 'stopped', label: '중단으로 기록', icon: CircleStop },
  { state: 'maintenance', label: '점검으로 기록', icon: Wrench },
]
const SUMMARY = [
  ['operating', '운영 중'], ['stopped', '중단'], ['maintenance', '점검'],
  ['drift', '불일치'], ['unverified', '확인 불가'], ['unset', '미설정'],
]
function headers(json = false) {
  const token = sessionStorage.getItem('token') || localStorage.getItem('token')
  return { ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}) }
}

function message(code) {
  return ({
    LOGIN_REQUIRED: '로그인이 필요합니다.',
    INVALID_SESSION: '로그인이 만료됐습니다. 다시 로그인해 주세요.',
    ADMIN_REQUIRED: '스카이넷 관리자 권한이 필요합니다.',
    AUTH_NOT_CONFIGURED: '관리자 인증 설정이 준비되지 않았습니다.',
    AUTH_UNAVAILABLE: '관리자 권한을 확인할 수 없습니다.',
    MONITOR_UNAVAILABLE: '운영 현황을 읽을 수 없습니다. 서버 상태를 확인해 주세요.',
    OPERATION_STATE_UNAVAILABLE: '운영 상태를 읽거나 저장할 수 없습니다.',
    OPERATION_REVISION_CONFLICT: '다른 곳에서 상태가 먼저 바뀌었습니다. 새로고침 후 다시 시도해 주세요.',
    PRODUCTION_APPROVAL_REQUIRED: '운영 환경 변경에는 승인 확인이 필요합니다.',
    INVALID_OPERATION_REQUEST: '변경할 상태와 사유를 확인해 주세요.',
    INVALID_OPERATION_TARGET: '서비스와 환경을 확인해 주세요.',
    SERVICE_NOT_FOUND: '등록된 서비스를 찾을 수 없습니다.',
    PODMAN_UNAVAILABLE: '이 서버의 컨테이너 상태를 읽지 못했습니다.',
  })[code] || '요청을 처리하지 못했습니다.'
}

async function api(path, options = {}) {
  const response = await fetch(`/api/deploy${path}`, {
    credentials: 'include', headers: headers(Boolean(options.body)), ...options,
  })
  let body
  try { body = await response.json() }
  catch { throw Error('응답을 읽을 수 없습니다.') }
  if (!response.ok) throw Error(message(body.code))
  return body
}

async function inventory(path, signal) {
  const response = await fetch(path, { credentials: 'include', headers: headers(), signal })
  if (!response.ok) throw Error('INVENTORY_UNAVAILABLE')
  return response.json()
}

function observedProcesses(snapshot) {
  const hosts = Array.isArray(snapshot?.hosts) ? snapshot.hosts
    : snapshot?.processes ? [{ hostname: '스카이넷 API 서버', processes: snapshot.processes }] : []
  return hosts.flatMap(host => host.unavailable || host.stale || host.truncated || host.verified === false
    ? [] : (Array.isArray(host.processes) ? host.processes : []).filter(item => item.status === 'online')
      .map(item => ({ runtime: 'PM2', host: host.hostname || '서버', name: item.name })))
}

function observedContainers(snapshot, runtime = 'Podman') {
  return (Array.isArray(snapshot?.containers) ? snapshot.containers : [])
    .filter(item => /^running$/i.test(item.state || ''))
    .map(item => ({ runtime, host: '스카이넷 API 서버', name: item.name }))
}

function time(value) {
  if (!value) return '시각 없음'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '시각 확인 불가' : date.toLocaleString('ko-KR')
}

function driftNotice(environment) {
  const { desiredState } = environment
  if (desiredState === 'running') return '운영으로 지정했지만 실행 대상이 준비되지 않았습니다.'
  return `${DESIRED_LABEL[desiredState]}으로 지정했지만 대상이 실행 중입니다.`
}

export default function DeployMonitor() {
  const [board, setBoard] = useState(null)
  const [inventoryState, setInventoryState] = useState({ processes: null, containers: null, docker: null })
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [loading, setLoading] = useState(false)
  const [checkedAt, setCheckedAt] = useState(null)
  const [live, setLive] = useState(true)
  const [filter, setFilter] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [expanded, setExpanded] = useState(null)
  const [draft, setDraft] = useState(null)
  const [saving, setSaving] = useState(false)
  const first = useRef(true)

  const load = useCallback(async signal => {
    setLoading(true)
    try {
      const body = await api('/monitor', { signal })
      if (signal?.aborted) return
      setBoard(body)
      setCheckedAt(new Date())
      setError('')
      const [processes, containers, docker] = await Promise.allSettled([
        inventory('/api/processes/snapshot', signal),
        inventory('/api/podman/snapshot', signal),
        inventory('/api/docker/snapshot', signal),
      ])
      if (!signal?.aborted) setInventoryState({
        processes: processes.status === 'fulfilled' ? processes.value : false,
        containers: containers.status === 'fulfilled' ? containers.value : false,
        docker: docker.status === 'fulfilled' ? docker.value : false,
      })
    } catch (cause) {
      if (cause.name !== 'AbortError' && !signal?.aborted) setError(cause.message)
    } finally { if (!signal?.aborted) setLoading(false) }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    if (first.current) { first.current = false; load(controller.signal) }
    else if (live) load(controller.signal)
    const timer = live ? setInterval(() => load(controller.signal), 10000) : null
    return () => { if (timer) clearInterval(timer); controller.abort() }
  }, [load, live])

  const services = board?.services || []
  const discovered = [
    ...observedProcesses(inventoryState.processes),
    ...observedContainers(inventoryState.containers),
    ...observedContainers(inventoryState.docker, 'Docker'),
  ]
  const inventoryIncomplete = Object.values(inventoryState).includes(false)
  const discoveredLabel = inventoryIncomplete
    ? (discovered.length ? `${discovered.length}개 이상` : '확인 불가')
    : Object.values(inventoryState).includes(null)
      ? '조회 중' : `${discovered.length}개`
  const counts = useMemo(() => {
    const tally = {}
    services.forEach(service => service.environments.forEach(environment => {
      tally[environment.status] = (tally[environment.status] || 0) + 1
    }))
    return tally
  }, [services])

  const visible = services.filter(service => (!filter || service.environments.some(item => item.status === filter))
    && `${service.displayName} ${service.serviceId} ${service.publicUrl || ''}`.toLowerCase().includes(search.trim().toLowerCase()))
  const environmentCount = services.reduce((total, service) => total + service.environments.length, 0)
  const attentionCount = ['drift', 'unverified'].reduce((total, state) => total + (counts[state] || 0), 0)
  const pageCount = Math.max(1, Math.ceil(visible.length / 10))
  const currentPage = Math.min(page, pageCount - 1)
  const pagedServices = visible.slice(currentPage * 10, currentPage * 10 + 10)

  async function apply(event) {
    event.preventDefault()
    if (!draft) return
    setSaving(true); setError(''); setNotice('')
    try {
      const body = await api(`/services/${encodeURIComponent(draft.serviceId)}/operation/${draft.environment}`, {
        method: 'PUT',
        body: JSON.stringify({ desiredState: draft.desiredState, expectedRevision: draft.revision,
          reason: draft.reason.trim(), approveProduction: draft.approve }),
      })
      setDraft(null)
      setNotice(`${draft.name} ${ENVIRONMENT_LABEL[draft.environment]} 환경을 ${DESIRED_LABEL[body.operation.desiredState]}으로 지정했습니다.`
        + (body.auditRecorded ? '' : ' 변경 기록 저장에는 실패했습니다.'))
      await load()
    } catch (cause) { setError(cause.message) }
    finally { setSaving(false) }
  }

  const openDraft = (service, environment, desiredState) => {
    setError(''); setNotice('')
    setDraft({ serviceId: service.serviceId, name: service.displayName,
      environment: environment.environment, desiredState,
      revision: environment.operation?.revision || 0, reason: '', approve: false })
  }

  return <main className="live-page">
    <header className="live-header">
      <div><span className="console-eyebrow">SERVER OPERATIONS</span><h1>서버 서비스</h1>
        <p>상시 제공하는 웹·API·서버의 개발과 운영 상태를 확인하세요. <Link to="/software">설치형 앱·소프트웨어 출시 →</Link></p></div>
      <div className="live-controls">
        <button type="button" onClick={() => load()} disabled={loading} aria-label="새로고침">
          <RefreshCw size={18} className={loading ? 'live-spin' : ''} /></button>
        <Link className="live-primary" to="/deploy">배포 관리</Link>
      </div>
    </header>

    {error && <p className="live-error" role="alert"><ShieldAlert size={16} />{error}</p>}
    {notice && <p className="live-notice" role="status"><Check size={16} />{notice}</p>}
    {board?.observation && !board.observation.available && <p className="live-warn" role="status">
      <AlertTriangle size={16} />{board.observation.truncated
        ? '컨테이너 목록이 일부만 조회됐습니다. 해당 대상의 상태는 확인이 필요합니다.'
        : `${message(board.observation.error)} 해당 대상의 상태를 확인해 주세요.`}</p>}

    <section className="live-overview" aria-label="서비스 요약">
      <div><span>등록 서비스<Layers size={17}/></span><strong>{board ? services.length : '—'}</strong><small>전체 서비스 카탈로그</small></div>
      <div><span>운영 중인 환경<Activity size={17}/></span><strong>{board ? counts.operating || 0 : '—'}<em> / {board ? environmentCount : '—'}</em></strong><small>개발·운영 환경의 관리 상태</small></div>
      <div className={attentionCount ? 'needs-attention' : ''}><span>확인 필요<AlertTriangle size={17}/></span><strong>{board ? attentionCount : '—'}</strong><small>상태 불일치 또는 관측 불가</small></div>
      <div><span>관리 목표 미설정<CircleDashed size={17}/></span><strong>{board ? counts.unset || 0 : '—'}</strong><small>장애 여부와 별도로 표시합니다</small></div>
    </section>

    <section className="live-workspace" aria-label="등록 서비스 목록">
      <div className="live-toolbar">
        <label className="live-search"><Search size={18} aria-hidden="true" /><input aria-label="서비스 검색" placeholder="서비스 이름 또는 도메인 검색" value={search} onChange={event => {setSearch(event.target.value);setPage(0)}} /></label>
        <select className="live-status-filter" aria-label="상태 필터" value={filter} onChange={event => {setFilter(event.target.value);setPage(0)}}>
          <option value="">모든 상태</option>{SUMMARY.map(([state,label]) => <option value={state} key={state}>{label}</option>)}
        </select>
      </div>
      <div className="live-table-head" aria-hidden="true"><span>서비스</span><span>개발 환경</span><span>운영 환경</span><span /></div>
      {!board && !error && <LoadingState label="운영 현황을 읽는 중…" />}
      {board && !services.length && <div className="live-empty"><Layers size={28} /><h3>아직 등록한 서비스가 없습니다</h3><p>실행 중인 서버 항목은 아래 발견 목록에서 확인할 수 있습니다.</p><Link to="/deploy">첫 서비스 등록</Link></div>}
      {board && Boolean(services.length) && !visible.length && <p className="live-empty">검색하거나 선택한 상태에 해당하는 서비스가 없습니다.</p>}
      <div className="live-grid">{pagedServices.map(service => <article key={service.serviceId} className={`live-card${expanded === service.serviceId ? ' is-expanded' : ''}`}>
        <div className="live-service-row">
          <div className="live-service-name"><span className="live-monogram" aria-hidden="true">{service.displayName.slice(0,2).toUpperCase()}</span><div><h3>{service.displayName}</h3><small>{service.publicUrl?.replace(/^https?:\/\//,'').replace(/\/$/,'') || service.serviceId}</small></div></div>
          {['development', 'production'].map(stage => {
            const environment = service.environments.find(item => item.environment === stage)
            const status = STATUS[environment?.status] || STATUS.unverified
            return <div key={stage} className="live-row-environment" aria-label={`${service.displayName} ${ENVIRONMENT_LABEL[stage]} 상태`}>
              <span className="live-mobile-label">{ENVIRONMENT_LABEL[stage]}</span>
              <span className={`live-status live-status-${status.tone}`}><i className="live-status-dot" />{status.label}</span>
              <small className="live-runtime">{({pm2:'PM2',k8s:'Kubernetes',podman:'Podman',docker:'Docker',static:'정적 사이트',systemd:'시스템 서비스',unconfigured:'미연결'})[environment?.target?.split(':')[0]] || environment?.runtime || '실행 대상 미등록'}</small>
            </div>
          })}
          <button type="button" className="live-expand" aria-label={`${service.displayName} 관리`} aria-expanded={expanded === service.serviceId}
            aria-controls={`service-${service.serviceId}`} onClick={() => { setExpanded(current => current === service.serviceId ? null : service.serviceId); setDraft(null) }}>
            상세<ChevronDown size={16} /></button>
        </div>
        {expanded === service.serviceId && <div id={`service-${service.serviceId}`} className="live-service-detail">
          <div className="live-detail-title"><span>환경 상세</span><div>{service.publicUrl && <a href={service.publicUrl} target="_blank" rel="noopener noreferrer">공개 주소<ExternalLink size={13} /></a>}<Link to={`/deploy?service=${service.serviceId}`}>배포 설정<Rocket size={13} /></Link></div></div>
          <div className="live-envs">{service.environments.map(environment => {
            const status = STATUS[environment.status] || STATUS.unset
            const EnvironmentMark = ENVIRONMENT_MARK[environment.environment] || Globe
            const editing = draft?.serviceId === service.serviceId && draft.environment === environment.environment
            return <section key={environment.environment} className={`live-env live-${status.tone}`} aria-label={`${service.displayName} ${ENVIRONMENT_LABEL[environment.environment]} 환경`}>
              <div className="live-env-head"><strong><EnvironmentMark size={15} />{ENVIRONMENT_LABEL[environment.environment]}</strong><span className={`live-status live-status-${status.tone}`}><i className="live-status-dot" />{status.label}</span></div>
              <dl className="live-facts"><div><dt>실제 상태</dt><dd>{environment.container?.status || OBSERVED_LABEL[environment.observedState] || '확인 불가'}</dd></div>
                <div><dt>관리 목표</dt><dd>{DESIRED_LABEL[environment.desiredState] || '미설정'}</dd></div>
                <div><dt>실행 대상</dt><dd>{environment.target || '미등록'}</dd></div>
                {environment.container?.image && <div><dt>이미지</dt><dd>{environment.container.image}</dd></div>}
                <div><dt>최근 배포</dt><dd>{environment.lastRun ? `${environment.lastRun.state} · ${time(environment.lastRun.createdAt)}` : '기록 없음'}</dd></div>
                {environment.operation?.reason && <div><dt>변경 사유</dt><dd>{environment.operation.reason}</dd></div>}
                {environment.operation && <div><dt>변경 시각</dt><dd>{time(environment.operation.changedAt)}</dd></div>}
              </dl>
              {environment.status === 'drift' && <p className="live-drift" role="alert"><AlertTriangle size={14} />{driftNotice(environment)}</p>}
              <details className="live-intent"><summary>관리 상태 기록</summary><p>관리 목표를 기록합니다. 실행 대상을 시작하거나 중단하는 명령은 아닙니다.</p>
                <div className="live-actions">{ACTIONS.map(action => <button key={action.state} type="button" disabled={saving || environment.desiredState === action.state}
                  onClick={() => openDraft(service, environment, action.state)}><action.icon size={14} />{action.label}</button>)}</div>
                {editing && <form className="live-form" onSubmit={apply}>
                  <p>{ENVIRONMENT_LABEL[draft.environment]} 환경을 <strong>{DESIRED_LABEL[draft.desiredState]}</strong>으로 지정합니다.</p>
                  <label>사유{draft.desiredState === 'running' ? ' (선택)' : ' (필수)'}<input value={draft.reason} maxLength={200} autoFocus required={draft.desiredState !== 'running'} placeholder="변경 사유를 입력하세요"
                    onChange={event => setDraft(current => ({ ...current, reason: event.target.value }))} /></label>
                  {draft.environment === 'production' && <label className="live-check"><input type="checkbox" checked={draft.approve} onChange={event => setDraft(current => ({ ...current, approve: event.target.checked }))} />운영 환경 변경을 승인합니다</label>}
                  <div className="live-form-actions"><button type="submit" disabled={saving || (draft.environment === 'production' && !draft.approve)}><Check size={14} />{saving ? '저장 중…' : '상태 적용'}</button><button type="button" className="live-ghost" onClick={() => setDraft(null)} disabled={saving}><X size={14} />취소</button></div>
                </form>}
              </details>
            </section>
          })}</div>
        </div>}
      </article>)}</div>
      {board && <nav className="live-pagination" aria-label="서비스 목록 페이지"><span>전체 {visible.length}개{visible.length>0 && ` 중 ${currentPage*10+1}–${Math.min((currentPage+1)*10,visible.length)}개`}</span><div><button type="button" disabled={currentPage===0} onClick={()=>{setPage(currentPage-1);setExpanded(null)}}>이전</button><span>{currentPage+1} / {pageCount}</span><button type="button" disabled={currentPage>=pageCount-1} onClick={()=>{setPage(currentPage+1);setExpanded(null)}}>다음</button></div></nav>}
      <div className="live-table-footer"><span role="status">{checkedAt ? `최근 확인 ${time(checkedAt)}` : '상태 확인 중'}</span><label className="live-toggle"><input type="checkbox" checked={live} onChange={event => setLive(event.target.checked)} />자동 갱신</label><Link to="/containers"><Clock size={14} />운영 이력</Link></div>
    </section>

    <div className="live-secondary">
      <details className="live-discovered"><summary><Layers size={16} />실행 중 발견한 항목 <span>{discoveredLabel}</span><ChevronDown size={15} /></summary>
        <div><p>서버에서 발견한 PM2·Podman·Docker 실행 항목입니다. 등록 서비스 목록과 별도로 관리합니다.</p>
          {inventoryIncomplete && <p role="status">일부 실행 목록을 읽지 못했습니다. 표시된 수는 전체 운영 서비스 수가 아닙니다.</p>}
          {discovered.length > 0 && <ul>{discovered.map((item, index) => <li key={`${item.runtime}:${item.host}:${item.name}:${index}`}><b>{item.runtime}</b><span>{item.name}</span><small>{item.host}</small></li>)}</ul>}
          {!discovered.length && inventoryState.processes !== null && inventoryState.containers !== null && <p>확인된 실행 항목이 없습니다.</p>}</div>
      </details>
      <details className="live-legend"><summary><HelpCircle size={16} />상태를 읽는 방법<ChevronDown size={15} /></summary><div>
        <p>PM2·Podman·Docker과 개발 Kubernetes의 최신 관측값을 관리 목표와 대조합니다. Kubernetes는 전체 복제본의 준비 상태를 확인합니다.</p>
        <p>실행 상태와 실제 앱 요청의 성공 여부는 별도입니다. ‘확인 불가’는 중단이나 0개를 뜻하지 않습니다.</p>
        <ul>{SUMMARY.map(([state, label]) => <li key={state}><span className={`live-status live-status-${STATUS[state].tone}`}><i className="live-status-dot" />{label}</span></li>)}</ul>
      </div></details>
    </div>
  </main>
}
